import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Plus, Tag, Users, Calendar, DollarSign, ChevronDown, ChevronUp, Trash2, UserPlus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPromosPage = () => {
  const { token } = useAuth();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [expandedPromo, setExpandedPromo] = useState(null);
  const [promoDetails, setPromoDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Form state for creating new promo
  const [newPromo, setNewPromo] = useState({
    name: '',
    price_per_card: '',
    label: '',
    expires_at: '',
    single_use: true,
    emails: ''
  });

  // State for adding emails to existing promo
  const [addEmailsText, setAddEmailsText] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPromos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/promos`, { headers });
      setPromos(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load promo lists');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromos();
  }, []);

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    
    // Parse emails from textarea
    const emailList = newPromo.emails
      .split(/[\n,]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    
    if (emailList.length === 0) {
      alert('Please enter at least one valid email address');
      return;
    }

    if (!newPromo.name || !newPromo.price_per_card || !newPromo.label || !newPromo.expires_at) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/api/admin/promos`, {
        name: newPromo.name,
        price_per_card: parseFloat(newPromo.price_per_card),
        label: newPromo.label,
        expires_at: new Date(newPromo.expires_at).toISOString(),
        single_use: newPromo.single_use,
        emails: emailList
      }, { headers });

      setCreateDialogOpen(false);
      setNewPromo({
        name: '',
        price_per_card: '',
        label: '',
        expires_at: '',
        single_use: true,
        emails: ''
      });
      fetchPromos();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create promo');
    }
  };

  const handleViewDetails = async (promoId) => {
    if (expandedPromo === promoId) {
      setExpandedPromo(null);
      setPromoDetails(null);
      return;
    }

    try {
      setLoadingDetails(true);
      setExpandedPromo(promoId);
      const response = await axios.get(`${API}/api/admin/promos/${promoId}`, { headers });
      setPromoDetails(response.data);
    } catch (err) {
      alert('Failed to load promo details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleAddEmails = async (promoId) => {
    const emailList = addEmailsText
      .split(/[\n,]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    
    if (emailList.length === 0) {
      alert('Please enter at least one valid email address');
      return;
    }

    try {
      const response = await axios.post(`${API}/api/admin/promos/${promoId}/emails`, {
        emails: emailList
      }, { headers });
      
      alert(`Added ${response.data.emails_added} email(s). ${response.data.emails_skipped} skipped (already in list).`);
      setAddEmailsText('');
      fetchPromos();
      handleViewDetails(promoId);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add emails');
    }
  };

  const handleApplyToExisting = async (promoId) => {
    if (!window.confirm('This will apply promo pricing to any existing users whose emails are in this list. Continue?')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/api/admin/promos/${promoId}/apply-to-existing`, {}, { headers });
      alert(response.data.message);
      fetchPromos();
      handleViewDetails(promoId);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to apply promo');
    }
  };

  const handleToggleActive = async (promoId, currentStatus) => {
    try {
      await axios.patch(`${API}/api/admin/promos/${promoId}?is_active=${!currentStatus}`, {}, { headers });
      fetchPromos();
    } catch (err) {
      alert('Failed to update promo status');
    }
  };

  const handleDeletePromo = async (promoId, promoName) => {
    if (!window.confirm(`Are you sure you want to delete "${promoName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/promos/${promoId}`, { headers });
      fetchPromos();
      if (expandedPromo === promoId) {
        setExpandedPromo(null);
        setPromoDetails(null);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete promo');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpired = (dateStr) => {
    return new Date(dateStr) < new Date();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Admin
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Tag className="h-6 w-6 text-[#003594]" />
                Promo Pricing
              </h1>
              <p className="text-gray-500 mt-1">Create and manage promotional pricing for collectors</p>
            </div>
            
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#003594] hover:bg-[#002554]" data-testid="create-promo-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Promo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Promo Pricing</DialogTitle>
                  <DialogDescription>
                    Set up special pricing for a group of collectors.
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleCreatePromo} className="space-y-4 mt-4">
                  <div>
                    <Label>Promo Name *</Label>
                    <Input
                      placeholder="e.g., Launch Special 2026"
                      value={newPromo.name}
                      onChange={(e) => setNewPromo({...newPromo, name: e.target.value})}
                      data-testid="promo-name-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Price Per Card ($) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="10.00"
                        value={newPromo.price_per_card}
                        onChange={(e) => setNewPromo({...newPromo, price_per_card: e.target.value})}
                        data-testid="promo-price-input"
                      />
                    </div>
                    <div>
                      <Label>Display Label *</Label>
                      <Input
                        placeholder="e.g., Launch Special"
                        value={newPromo.label}
                        onChange={(e) => setNewPromo({...newPromo, label: e.target.value})}
                        data-testid="promo-label-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Shown to user on submission page</p>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Expiration Date *</Label>
                    <Input
                      type="date"
                      value={newPromo.expires_at}
                      onChange={(e) => setNewPromo({...newPromo, expires_at: e.target.value})}
                      data-testid="promo-expiry-input"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Single Use</Label>
                      <p className="text-xs text-gray-500">Only applies to first submission</p>
                    </div>
                    <Switch
                      checked={newPromo.single_use}
                      onCheckedChange={(checked) => setNewPromo({...newPromo, single_use: checked})}
                    />
                  </div>
                  
                  <div>
                    <Label>Email Addresses *</Label>
                    <textarea
                      className="w-full min-h-[120px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#003594]"
                      placeholder="Enter email addresses (one per line or comma-separated)"
                      value={newPromo.emails}
                      onChange={(e) => setNewPromo({...newPromo, emails: e.target.value})}
                      data-testid="promo-emails-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {newPromo.emails.split(/[\n,]+/).filter(e => e.trim() && e.includes('@')).length} valid email(s)
                    </p>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-[#003594] hover:bg-[#002554]">
                      Create Promo
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading promos...</div>
        ) : promos.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              No promo lists created yet. Click "Create Promo" to get started.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {promos.map((promo) => (
              <Card key={promo.id} className={`${!promo.is_active || isExpired(promo.expires_at) ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{promo.name}</CardTitle>
                        {!promo.is_active && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Inactive</span>
                        )}
                        {isExpired(promo.expires_at) && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Expired</span>
                        )}
                      </div>
                      <CardDescription className="mt-1">
                        <span className="font-medium text-green-600">${promo.price_per_card}/card</span>
                        <span className="mx-2">•</span>
                        <span>"{promo.label}"</span>
                        <span className="mx-2">•</span>
                        <span>{promo.single_use ? 'Single use' : 'Unlimited'}</span>
                      </CardDescription>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(promo.id, promo.is_active)}
                        className="text-gray-500"
                      >
                        {promo.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePromo(promo.id, promo.name)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{promo.total_eligible} pending</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        <span>{promo.total_claimed} claimed</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>Expires {formatDate(promo.expires_at)}</span>
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(promo.id)}
                    >
                      {expandedPromo === promo.id ? (
                        <>
                          <ChevronUp className="h-4 w-4 mr-1" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4 mr-1" />
                          View Details
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedPromo === promo.id && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      {loadingDetails ? (
                        <div className="text-center py-4 text-gray-500">Loading...</div>
                      ) : promoDetails ? (
                        <div className="space-y-6">
                          {/* Add More Emails */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                              <UserPlus className="h-4 w-4" />
                              Add More Emails
                            </h4>
                            <textarea
                              className="w-full min-h-[80px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#003594] bg-white"
                              placeholder="Enter email addresses (one per line or comma-separated)"
                              value={addEmailsText}
                              onChange={(e) => setAddEmailsText(e.target.value)}
                            />
                            <div className="flex justify-between items-center mt-2">
                              <p className="text-xs text-gray-500">
                                {addEmailsText.split(/[\n,]+/).filter(e => e.trim() && e.includes('@')).length} valid email(s)
                              </p>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleApplyToExisting(promo.id)}
                                >
                                  Apply to Existing Users
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleAddEmails(promo.id)}
                                  className="bg-[#003594] hover:bg-[#002554]"
                                >
                                  Add Emails
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Pending Emails */}
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">
                              Pending Emails ({promoDetails.emails?.length || 0})
                            </h4>
                            {promoDetails.emails?.length > 0 ? (
                              <div className="bg-white border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                                <div className="flex flex-wrap gap-2">
                                  {promoDetails.emails.map((email, i) => (
                                    <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                      {email}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">All emails have been claimed</p>
                            )}
                          </div>
                          
                          {/* Claimed Emails */}
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">
                              Claimed Emails ({promoDetails.claimed_emails?.length || 0})
                            </h4>
                            {promoDetails.claimed_emails?.length > 0 ? (
                              <div className="bg-white border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                                <div className="flex flex-wrap gap-2">
                                  {promoDetails.claimed_emails.map((email, i) => (
                                    <span key={i} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                                      {email}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No emails claimed yet</p>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPromosPage;
